import { useEffect, useRef, useState } from 'react';

type UseTreeIntersectionObserverOptions = {
  /**
   * CSS selector string for elements to observe (e.g., "#section1, #section2").
   * Can also be passed as an array of selectors.
   */
  elementsToObserve: string | string[];
  /**
   * The element that is used as the viewport for checking visibility.
   * If null, uses the browser viewport.
   * @default null
   */
  root?: Element | null;
  /**
   * Margin around the root element.
   * @default '15% 0px -40% 0px'
   */
  rootMargin?: string;
  /**
   * How much of the target element must be visible.
   * @default 0.5
   */
  threshold?: number;
};

/**
 * Custom hook for tracking which element in a tree is currently visible.
 * Uses IntersectionObserver to detect when elements enter/leave the viewport.
 * When several are visible at once, the first one in document order is focused.
 *
 * @param options - Configuration options for the observer
 * @returns Object containing the ID of the currently focused element
 */
export function useTreeIntersectionObserver({
  elementsToObserve,
  root = null,
  rootMargin = '15% 0px -40% 0px',
  threshold = 0.5,
}: UseTreeIntersectionObserverOptions) {
  const observer = useRef<IntersectionObserver | null>(null);
  const [focusElementID, setFocusElementID] = useState('');

  // Convert array to comma-separated string if needed
  const selectorString = Array.isArray(elementsToObserve)
    ? elementsToObserve.join(', ')
    : elementsToObserve;

  useEffect(() => {
    // Skip if no elements to observe
    if (!selectorString) return;

    const elements = [...document.querySelectorAll(selectorString)];

    // An observer callback only reports the targets that crossed a threshold,
    // so the targets still in view have to be tracked across callbacks. The
    // topmost of them is the focused one: with thin targets, several are in
    // view at once and the last one to cross must not win.
    const inView = new Set<Element>();

    observer.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            inView.add(entry.target);
          } else {
            inView.delete(entry.target);
          }
        }

        const focusedElement = elements.find((element) => inView.has(element));
        if (focusedElement) {
          setFocusElementID(focusedElement.id);
        }
      },
      {
        root,
        rootMargin,
        threshold,
      },
    );

    elements.forEach((el) => observer.current?.observe(el));

    return () => {
      observer.current?.disconnect();
    };
  }, [selectorString, root, rootMargin, threshold]);

  return { focusElementID };
}
